import { inject, Injectable, ApplicationRef, Type, Injector } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ZnModalWrapper } from '../../zn-modal-wrapper/zn-modal-wrapper';
import { IModal } from '../interfaces/IModal';
import { IModalContent } from '../interfaces/IModalContent';
import { isObservable, take } from 'rxjs';
 
interface InputFactoryConfig {
  useFactory: (...args: any[]) => any;
  deps: any[];
}

@Injectable({
  providedIn: 'root',
})
export class ModalBuilder {
  private readonly modalService = inject(NgbModal);
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(Injector);

  openModal<T>(componentClass: Type<T>, configuration: IModal, inputs?: Record<string, any>) {
    const modalRef = this.modalService.open(ZnModalWrapper, {
      size: configuration.size ?? 'md',
      backdrop: configuration.backdrop ?? 'static',
      windowClass: configuration.asideConfig?.show? 'zn-modal-with-aside': null,
    });

    modalRef.componentInstance.component = componentClass;
    modalRef.componentInstance.configuration = configuration;
    modalRef.componentInstance.componentInputs = this.resolveInputs(configuration.inputs ?? {});
    
    this.appRef.tick();
    this.bindCompletion(modalRef, configuration);

    return modalRef;
  }

  private bindCompletion(modalRef: any, configuration: IModal): void {
    if (!configuration.onCompleted && !configuration.afterClosed) {
      return;
    }

    if (configuration.afterClosed) {
      modalRef.closed
        ?.pipe(take(1))
        .subscribe((result: unknown) => {
          const complete = () => configuration.onCompleted?.();
          const execution = configuration.afterClosed?.(result);

          if (isObservable(execution)) {
            execution.subscribe({
              complete,
              error: error => {
                console.error(error);
                complete();
              },
            });
            return;
          }

          if (this.isPromiseLike(execution)) {
            void execution
              .then(() => complete())
              .catch(error => {
                console.error(error);
                complete();
              });
            return;
          }

          complete();
        });
      return;
    }

    const contentInstance = this.getModalContentInstance(modalRef);

    if (contentInstance?.completed) {
      contentInstance.completed.subscribe(() => {
        configuration.onCompleted?.();
      });

      return;
    }

    modalRef.closed
      ?.pipe(take(1))
      .subscribe(() => configuration.onCompleted?.());
  }

  private getModalContentInstance(modalRef: any): IModalContent | undefined {
    const wrapperInstance = modalRef?.componentInstance as ZnModalWrapper | undefined;

    if (!wrapperInstance || typeof wrapperInstance.outlet !== 'function') {
      return undefined;
    }

    return wrapperInstance.outlet()?.componentInstance as IModalContent | undefined;
  }

  private isPromiseLike(value: unknown): value is Promise<unknown> {
    return !!value && typeof (value as Promise<unknown>).then === 'function';
  }

  private resolveInputs(inputs: Record<string, any>): Record<string, any> {
    return Object.entries(inputs).reduce((acc, [key, value]) => {
      acc[key] = this.tryResolve(value);
      return acc;
    }, {} as Record<string, any>);
  }

  private tryResolve(value: any): any {
    if (this.isInputFactoryConfig(value)) {
      const deps = value.deps.map(dep => this.tryResolve(dep));
      return value.useFactory(...deps);
    }

    if (typeof value === 'function' && value.prototype) {
      try {
        return this.injector.get(value);
      } catch {
        return value;
      }
    }
    return value;
  }

  private isInputFactoryConfig(value: any): value is InputFactoryConfig {
    return !!value
      && typeof value === 'object'
      && typeof value.useFactory === 'function'
      && Array.isArray(value.deps);
  }
}
